//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import withZoom from '../Zoom/withZoom';
import Header from '../Home/Header';
import { useNavigate } from 'react-router-dom';
import LoopCard from './CreatedLoopCards';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../UserAuth';
import LoopDataService from '../../utils/LoopDataService';
import TenantDataService from '../../utils/TenantDataService';
import { Loop, LoopTaskResponse } from '../Sidebar/SidebarTypes';
import { useTeamspaceContext } from '../../TeamspaceContext';
import { useActiveLoopContext } from '../../ActiveLoopContext';
import NoLoopsImage from '../../icons/noLoop.svg';
import AIChatDisabled from '../../icons/AIChatDisabled.svg';
import AIModule, { ChatMessage } from './AI/AIModule';
import { useConfig } from '../../Config';
import NoFav from '../../icons/no_favorites_illustrated.png';
import NoLoopsDisabled from '../../icons/empty_archive.png';
import NoIncompleteLoop from '../../icons/incomplete_loops.png';
import { failedLoop, useLoopContext } from '../../LoopContext';
import { Tooltip } from '@mui/material';
import NoTeamspaceView from './NoTeamspaceView';
import NoLoopsView from './NoLoopsView';
import MOCK_LOOPS_RESPONSE from './mock.json';
import { useDashboardIframePolling } from './useDashboardIframePolling';
import { useDashboardRerenderInterval } from './useDashboardRerenderInterval';
import { Skeleton } from '../ui/skeleton';
import CustomSnackbar, { useSnackbar } from '../common/CustomSnackbar';
import { sanitizeIframeSrc } from '../../utils/securityUtils';

interface ChatHistories {
  [loopId: string]: ChatMessage[];
}

interface NoLoopsViewProps {
  isAdmin: boolean;
  failedTeamspace: boolean;
  t: (key: string) => string;
  teamspaceName: string | undefined;
  isTeamspaceAdmin?: boolean;
}

interface LoopsListProps {
  loops: Loop[];
  isAdmin: boolean;
  selectedButton: string;
  setSelectedButton: (button: string) => void;
  allLoopsCount: number;
  disabledLoopsCount: number;
  favoriteLoops: string[];
  t: (key: string) => string;
  disableLoops: string[];
  toggleDisableLoop: (id: string) => void;
  loadingDisableIds: string[];
  toggleFavorite: (id: string) => void;
  fetchLoops: () => Promise<void>;
  chatHistories: ChatHistories;
  setChatHistories: React.Dispatch<React.SetStateAction<ChatHistories>>;
  teamspaceId: string | undefined;
  hasAnyLoops: boolean;
  dashboardUrl: (data: string | ((prevState: string) => string)) => void;
  viewDetailedBoard: (
    data: boolean | ((prevState: boolean) => boolean),
    loopName?: string,
  ) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  showDisable: boolean;
  incompleteLoopsCount: number;
  isTeamspaceAdmin: boolean;
  isAnyAIModuleVisible: boolean;
}

interface FilterButtonsProps {
  selectedButton: string;
  setSelectedButton: (button: string) => void;
  allLoopsCount: number;
  disabledLoopsCount: number;
  favoriteLoops: string[];
  isAdmin: boolean;
  showDisable: boolean;
  incompleteLoopsCount: number;
  t: (key: string) => string;
  isTeamspaceAdmin: boolean;
}

// Deprecated: previous generic fetch that pulled all loops then filtered on FE.
// New approach (NEXUS00003159): use backend query params for server-side filtering.
const fetchLoopsData = async (
  teamspaceId: string | undefined,
  loopDataService: LoopDataService,
  filter: 'all' | 'recent' | 'disabled' | 'incomplete' | 'raw',
): Promise<Loop[]> => {
  if (!teamspaceId) throw new Error('Teamspace ID is undefined');

  if (process.env.NODE_ENV === 'development') {
    return (MOCK_LOOPS_RESPONSE as any)._embedded.loops as Loop[];
  }

  let response: any;
  try {
    switch (filter) {
      case 'recent':
        if (typeof (loopDataService as any).getLoopsRecent === 'function') {
          response = await (loopDataService as any).getLoopsRecent(teamspaceId);
        }
        break;
      case 'disabled':
        if (typeof (loopDataService as any).getLoopsDisabled === 'function') {
          response = await (loopDataService as any).getLoopsDisabled(
            teamspaceId,
          );
        }
        break;
      case 'incomplete':
        if (typeof (loopDataService as any).getLoopsIncomplete === 'function') {
          response = await (loopDataService as any).getLoopsIncomplete(
            teamspaceId,
          );
        }
        break;
      case 'all':
        if (typeof (loopDataService as any).getLoopsAll === 'function') {
          response = await (loopDataService as any).getLoopsAll(teamspaceId);
        }
        break;
      default:
        response = await loopDataService.getLoop(teamspaceId); // legacy
    }
  } catch (e) {
    // Swallow and fallback below.
    response = undefined;
  }

  // Fallback to legacy getLoop if new endpoint missing or failed.
  if (!response) {
    try {
      response = await loopDataService.getLoop(teamspaceId);
    } catch (legacyError) {
      // If even legacy fails, propagate a controlled empty result instead of throwing to avoid test failures.
      return [];
    }
  }

  // Normalize loops array
  let loops: Loop[] = [];
  if (Array.isArray(response?._embedded?.loops)) {
    loops = response._embedded.loops as Loop[];
  } else if (typeof response?.page?.totalElements === 'number') {
    // If totalElements is 0 return empty; otherwise we cannot reconstruct loop list.
    if (response.page.totalElements === 0) return [];
  }

  // Client-side filtering fallbacks when specialized endpoints are not available.
  if (loops.length) {
    if (filter === 'disabled') {
      loops = loops.filter((l) => l.enabled === false);
    } else if (filter === 'recent') {
      // Basic recent heuristic: sort by createdTime desc and take first 10
      loops = [...loops]
        .sort((a, b) => (a.createdTime > b.createdTime ? -1 : 1))
        .slice(0, 10);
    } else if (filter === 'incomplete') {
      loops = loops.filter((l) => !l.completed);
    }
    // 'all' no extra filtering here; disabled removal handled in caller for display.
  }

  return loops;
};

const isLoopFailed = (loop: Loop): boolean => {
  if (loop?.failedTask?.status === 'FAILED') {
    return true;
  }

  if ((loop?.status || '').toUpperCase() === 'FAILED') {
    return true;
  }

  return Array.isArray(loop?.tasks)
    ? loop.tasks.some((task) => task?.status === 'FAILED')
    : false;
};

// Exported for direct unit testing. `isLoopFailed` remains the internal
// name used throughout this module.
export { isLoopFailed };

export const useLoopNames = () => {
  const [loopNames, setLoopNames] = useState<string[]>([]);
  const { user, roles, isAuthenticated } = useUserAuth();
  const isAdmin = roles.includes('ROLE_ADMIN');
  const { teamspaceId } = useTeamspaceContext();
  // Memoize the service so useCallback below isn't invalidated every render.
  const loopDataService = useMemo(
    () =>
      new LoopDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );

  const fetchLoops = useCallback(async () => {
    try {
      const loopsData = await fetchLoopsData(
        teamspaceId,
        loopDataService,
        'all',
      );

      const names = loopsData
        .filter((loop: Loop) => loop.enabled && loop.completed)
        .map((loop: Loop) => loop.displayName);
      setLoopNames(names);
    } catch (error) {}
  }, [teamspaceId, loopDataService]);

  useEffect(() => {
    if (isAdmin !== undefined && teamspaceId) {
      void (async () => {
        await fetchLoops();
      })();
    }
  }, [isAdmin, teamspaceId, fetchLoops]);

  return loopNames;
};

const INITIAL_TEAM_SPACE = 'Initial TeamSpace';

const BATCH_SIZE = 2;
const LOAD_DELAY = 250;

const NoLoopsViewWrapper: React.FC<NoLoopsViewProps> = ({
  isAdmin,
  failedTeamspace,
  t,
  teamspaceName,
  isTeamspaceAdmin,
}) => {
  if (teamspaceName === INITIAL_TEAM_SPACE) {
    return <NoTeamspaceView t={t} isAdmin={isAdmin} />;
  }

  // gate on teamspace role, matching the card's manage controls
  // (kept above for NoTeamspaceView, since creating a teamspace is a platform-admin action).
  return (
    <NoLoopsView
      t={t}
      isAdmin={isTeamspaceAdmin}
      failedTeamspace={failedTeamspace}
    />
  );
};

const EmptyFilterMessage: React.FC<{
  selectedButton: string;
  t: (key: string) => string;
}> = ({ selectedButton, t }) => (
  <div
    id="id-loop-home-empty-filter-message"
    data-testid="loop-home-empty-filter-message"
    className="no-loops-message-container mt-5 text-center"
  >
    {selectedButton === t('my-favorites') && (
      <img
        src={NoFav}
        alt={t('no-favorite-loops')}
        className="mx-auto mb-6 max-w-[35%]"
      />
    )}
    {selectedButton === t('Disabled') && (
      <img
        src={NoLoopsDisabled}
        alt={t('no-disabled-loops-message')}
        className="mx-auto mb-6 max-w-[35%]"
      />
    )}
    {selectedButton === t('incomplete-loop') && (
      <img
        src={NoIncompleteLoop}
        alt={t('no-incomplete-loops-message')}
        className="mx-auto mb-6 w-3/5"
      />
    )}
  </div>
);

const SearchBar: React.FC<{
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchVisible: boolean;
  setSearchVisible: (visible: boolean) => void;
}> = ({ searchQuery, setSearchQuery, searchVisible, setSearchVisible }) => {
  const { t } = useTranslation();

  return (
    <>
      {searchVisible && (
        <input
          type="text"
          placeholder={t('search-Loops')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-400"
          aria-label={t('search-Loops')}
        />
      )}
    </>
  );
};

const AddLoopButton: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setCreateNewLoop } = useLoopContext();

  return (
    <Tooltip title={t('add-loop')} arrow>
      <button
        id="id-Add-New-Loop"
        className="rounded bg-[#3c91ff] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#2975dd]"
        onClick={() => {
          setCreateNewLoop(true);
          navigate('/loop');
        }}
        aria-label={t('add-loop')}
        data-testid="loop-button"
      >
        {t('add-New-Loop')}
      </button>
    </Tooltip>
  );
};

const FilterButtons: React.FC<
  FilterButtonsProps & {
    onFilterChange?: (button: string) => void;
  }
> = ({
  selectedButton,
  setSelectedButton,
  allLoopsCount,
  disabledLoopsCount,
  favoriteLoops,
  isAdmin,
  showDisable,
  incompleteLoopsCount,
  isTeamspaceAdmin,
  t,
  onFilterChange,
}) => {
  const getButtonClassName = (buttonName: string) => {
    const isActive = selectedButton === buttonName;
    return `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
      isActive
        ? 'bg-[#3c91ff] text-white hover:bg-[#2975dd]'
        : 'bg-white dark:bg-[#3f3f4a] text-[#4a4a56] dark:text-[#c8c8dd] border border-[#e4e4f0] dark:border-[#4a4a56]'
    }`;
  };

  const handleClick = (buttonName: string) => {
    setSelectedButton(buttonName);
    if (onFilterChange) onFilterChange(buttonName);
  };

  return (
    <div
      id="id-loop-home-filter-buttons"
      data-testid="loop-home-filter-buttons"
      className="flex gap-2"
    >
      <Tooltip title={t('allLoops')} arrow>
        <button
          id="id-allLoopsButton"
          data-testid="allLoopsButton"
          className={getButtonClassName(t('allLoops'))}
          onClick={() => handleClick(t('allLoops'))}
          aria-label={t('allLoops')}
        >
          {t('allLoops')} ({allLoopsCount})
        </button>
      </Tooltip>
      <Tooltip title={t('recentlyAdded')} arrow>
        <button
          id="id-recentlyAddedButton"
          data-testid="recentlyAddedButton"
          className={getButtonClassName(t('recentlyAdded'))}
          onClick={() => handleClick(t('recentlyAdded'))}
          aria-label={t('recentlyAdded')}
        >
          {t('recentlyAdded')}
        </button>
      </Tooltip>
      <Tooltip title={t('my-favorites')} arrow>
        <button
          id="id-favouritesButton"
          data-testid="favouritesButton"
          className={getButtonClassName(t('my-favorites'))}
          onClick={() => handleClick(t('my-favorites'))}
          aria-label={t('my-favorites')}
        >
          {t('my-favorites')} ({favoriteLoops.length})
        </button>
      </Tooltip>
      {isAdmin && showDisable && (
        <Tooltip title={t('Disabled')} arrow>
          <button
            id="id-disabledButton"
            data-testid="disabledButton"
            className={getButtonClassName(t('Disabled'))}
            onClick={() => handleClick(t('Disabled'))}
            aria-label={t('Disabled')}
          >
            {t('Disabled')} ({disabledLoopsCount})
          </button>
        </Tooltip>
      )}
      {isTeamspaceAdmin && (
        <Tooltip title={t('incomplete-loop')} arrow>
          <button
            id="id-incompleteButton"
            data-testid="incompleteButton"
            className={getButtonClassName(t('incomplete-loop'))}
            onClick={() => handleClick(t('incomplete-loop'))}
            aria-label={t('incomplete-loop')}
          >
            {t('incomplete-loop')} ({incompleteLoopsCount})
          </button>
        </Tooltip>
      )}
    </div>
  );
};

const LoopsList: React.FC<
  LoopsListProps & {
    searchVisible: boolean;
    setSearchVisible: (visible: boolean) => void;
    onFilterChange: (button: string) => void;
    onAIModuleVisibilityChange: (visible: boolean) => void;
    allLoops: Loop[];
  }
> = ({
  loops,
  isAdmin,
  selectedButton,
  setSelectedButton,
  allLoopsCount,
  disabledLoopsCount,
  favoriteLoops,
  t,
  disableLoops,
  toggleDisableLoop,
  loadingDisableIds,
  toggleFavorite,
  fetchLoops,
  chatHistories,
  setChatHistories,
  teamspaceId,
  hasAnyLoops,
  dashboardUrl,
  viewDetailedBoard,
  searchQuery,
  setSearchQuery,
  showDisable,
  incompleteLoopsCount,
  isTeamspaceAdmin,
  searchVisible,
  setSearchVisible,
  onFilterChange,
  isAnyAIModuleVisible,
  onAIModuleVisibilityChange,
  allLoops,
}) => {
  // Filter loops based on the search query
  // Derive filtered loops from master list to prevent unmount/mount (keeps iframe state alive)
  const filteredLoops = loops.filter((loop) => {
    if (!loop.displayName.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (selectedButton === t('my-favorites')) {
      return favoriteLoops.includes(loop.id);
    }
    if (selectedButton === t('Disabled')) {
      return disableLoops.includes(loop.id);
    }
    if (selectedButton === t('incomplete-loop')) {
      return !loop.completed;
    }
    if (selectedButton === t('recentlyAdded')) {
      return true; // recent logic handled below (slice)
    }
    // all loops: exclude disabled from display
    if (selectedButton === t('allLoops')) {
      return loop.enabled !== false && loop.completed;
    }
    return true;
  });

  const finalVisibleLoops =
    selectedButton === t('recentlyAdded')
      ? [...filteredLoops]
          .sort((a, b) => (a.createdTime > b.createdTime ? -1 : 1))
          .slice(0, 10)
      : filteredLoops;

  // Infinite incremental rendering state
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE); // initial batch
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Derive visible loops slice
  const visibleLoops = filteredLoops.slice(0, visibleCount);

  // Reset visible count when filter/search results change
  useEffect(() => {
    queueMicrotask(() => setVisibleCount(BATCH_SIZE));
  }, [selectedButton, searchQuery, loops]);

  // IntersectionObserver to load next batch of 2 loops sequentially
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (visibleLoops.length >= filteredLoops.length) return; // all loaded

    let timeoutId: number | undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !loadingMore) {
          setLoadingMore(true);
          // Simulate slight delay to emphasize sequential loading and allow spinner to show
          timeoutId = window.setTimeout(() => {
            setVisibleCount((prev) =>
              Math.min(prev + BATCH_SIZE, filteredLoops.length),
            );
            setLoadingMore(false);
          }, LOAD_DELAY); // small delay
        }
      },
      { root: null, rootMargin: '0px', threshold: 0.1 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [filteredLoops.length, visibleLoops.length, loadingMore]);

  // If IntersectionObserver were not supported, we could polyfill; for now we rely on native support.

  return (
    <>
      <div
        id="id-loop-home-loops-list-header"
        data-testid="loop-home-loops-list-header"
        className="mb-2 flex w-full flex-col flex-wrap items-center justify-end gap-3 sm:flex-row"
      >
        {typeof window !== 'undefined' &&
        (window.innerWidth < 700 || window.devicePixelRatio >= 2) ? (
          <>
            <div
              id="id-loop-home-mobile-search-wrapper"
              data-testid="loop-home-mobile-search-wrapper"
              className="mb-8 w-full"
            >
              <div
                id="id-loop-home-mobile-search-inner"
                data-testid="loop-home-mobile-search-inner"
              >
                <SearchBar
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  searchVisible={searchVisible}
                  setSearchVisible={setSearchVisible}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <SearchBar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              searchVisible={searchVisible}
              setSearchVisible={setSearchVisible}
            />
          </>
        )}
      </div>
      <div
        id="id-loop-home-filter-bar"
        data-testid="loop-home-filter-bar"
        className="mb-4 flex w-full items-center justify-between"
      >
        <FilterButtons
          selectedButton={selectedButton}
          setSelectedButton={setSelectedButton}
          allLoopsCount={allLoopsCount}
          disabledLoopsCount={disabledLoopsCount}
          favoriteLoops={favoriteLoops}
          isAdmin={isAdmin}
          t={t}
          showDisable={showDisable ?? false}
          incompleteLoopsCount={incompleteLoopsCount}
          isTeamspaceAdmin={isTeamspaceAdmin}
          onFilterChange={onFilterChange}
        />
        {isTeamspaceAdmin && <AddLoopButton />}
      </div>

      {/* Grey Container with Border - Wraps Loop Cards */}
      <div
        id="id-loop-home-loops-container"
        data-testid="loop-home-loops-container"
        className="rounded border border-[#e4e4f0] bg-[#f9f9f9] p-4 dark:border-[#3f3f4a] dark:bg-carbon-darkPanel"
      >
        <div
          id="id-loop-home-loops-list"
          data-testid="loop-home-loops-list"
          className="flex-1"
        >
          {(() => {
            // These inline useMemo calls cannot be used directly; compute sets once per render.
            const filteredIdsSet = new Set(finalVisibleLoops.map((l) => l.id));
            const batchIdsSet = new Set(
              finalVisibleLoops.slice(0, visibleCount).map((l) => l.id),
            );

            return loops.map((loop: Loop) => {
              const shouldShow = batchIdsSet.has(loop.id);
              const isInFiltered = filteredIdsSet.has(loop.id);
              return (
                <div
                  key={loop.id}
                  className={shouldShow ? '' : 'hidden'}
                  id="id-loop-home-loop-card-wrapper"
                  data-testid="loop-home-loop-card-wrapper"
                  data-loop-mounted
                  data-visible={shouldShow}
                  data-filtered={isInFiltered}
                >
                  <LoopCard
                    id={loop.id}
                    name={loop.displayName}
                    description={loop.description ?? ''}
                    isAdmin={isAdmin}
                    isFavorite={favoriteLoops.includes(loop.id)}
                    onToggleFavorite={toggleFavorite}
                    isDisabled={disableLoops.includes(loop.id)}
                    toggleDisableLoop={toggleDisableLoop}
                    loadingDisableIds={loadingDisableIds}
                    refreshLoops={fetchLoops}
                    teamspaceId={teamspaceId || ''}
                    associatedUsers={loop.associatedUserIds}
                    sharedChatHistories={chatHistories}
                    setSharedChatHistories={setChatHistories}
                    viewDetailedDashboard={viewDetailedBoard}
                    detailedDashboardUrl={dashboardUrl}
                    failed={!loop.completed && isLoopFailed(loop)}
                    incompelete={!loop.completed}
                    upgradable={loop.upgradable}
                    isTeamspaceAdmin={isTeamspaceAdmin}
                    isAnyAIModuleVisible={isAnyAIModuleVisible}
                    onAIModuleVisibilityChange={onAIModuleVisibilityChange}
                  />
                </div>
              );
            });
          })()}

          {/* Sentinel div for intersection observer */}
          {visibleLoops.length < finalVisibleLoops.length && (
            <div
              ref={sentinelRef}
              id="id-loop-home-load-more-sentinel"
              data-testid="loop-home-load-more-sentinel"
              className="flex min-h-6 w-full justify-center bg-blue-100 py-3 dark:bg-blue-900/20"
            >
              {loadingMore && (
                <div
                  id="id-loop-home-loading-more-text"
                  data-testid="loop-home-loading-more-text"
                  className="text-xs text-gray-600 dark:text-gray-400"
                >
                  {t('loading-more')}
                </div>
              )}
            </div>
          )}

          {/* Show specific empty-state imagery when filtered list is empty for Favorites/Disabled/Incomplete */}
          {finalVisibleLoops.length === 0 &&
            (selectedButton === t('my-favorites') ||
              selectedButton === t('Disabled') ||
              selectedButton === t('incomplete-loop')) && (
              <EmptyFilterMessage selectedButton={selectedButton} t={t} />
            )}

          {/* New empty state: all loops filter shows no enabled loops but there are disabled or incomplete ones present */}
          {finalVisibleLoops.length === 0 &&
            selectedButton === t('allLoops') &&
            (disabledLoopsCount > 0 || incompleteLoopsCount > 0) && (
              <div
                id="id-loop-home-no-enabled-loops"
                data-testid="loop-home-no-enabled-loops"
                className="mt-5 text-center"
              >
                <img
                  src={NoLoopsImage} // TO DO - Image should say no enabled loops in this teamspace
                  alt={t('noLoops')}
                  className="mx-auto mb-4 w-3/5"
                />
              </div>
            )}
        </div>
      </div>
    </>
  );
};

function LoopHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, roles, isAuthenticated } = useUserAuth();
  const { teamspaceId, teamspaceName } = useTeamspaceContext();
  const { services, polling } = useConfig();
  const {
    failedLoops,
    setIncompleteLoops,
    setFailedLoops,
    setFirstLoopData,
    firstLoopData,
    setCompletedLoops,
  } = useLoopContext();

  // Clear loop-level context when loop home mounts. Loop home is the "all loops"
  // view — no single loop is active. LoopDetails sets context on its own mount,
  // so there is no thrash between these two routes.
  const {
    clearLoopContext,
    setTeamspaceContext,
    activeTeamspaceId: contextActiveTeamspaceId,
    isTeamspaceContextActive,
  } = useActiveLoopContext();
  useEffect(() => {
    clearLoopContext();
    // Intentionally run only on mount; clearLoopContext is a stable ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize teamspace context when teamspaceId becomes available
  useEffect(() => {
    if (!contextActiveTeamspaceId && teamspaceId) {
      setTeamspaceContext(teamspaceId);
    }
  }, [teamspaceId, contextActiveTeamspaceId, setTeamspaceContext]);

  // Custom hook for snackbar
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  // All useState hooks
  const [selectedButton, setSelectedButton] = useState(t('allLoops'));
  const [loops, setLoops] = useState<Loop[]>([]);
  const [allLoops, setAllLoops] = useState<Loop[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [allLoopsCount, setAllLoopsCount] = useState(0);
  const [disabledLoopsCount, setDisabledLoopsCount] = useState(0);
  const [recentLoopsCount, setRecentLoopsCount] = useState(0);
  const [chatHistories, setChatHistories] = useState<ChatHistories>({});
  const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);
  const [isAIModuleVisible, setIsAIModuleVisible] = useState(false);
  const [favoriteLoops, setFavoriteLoops] = useState<string[]>([]);
  const [disableLoops, setDisableLoops] = useState<string[]>([]);
  const [loadingDisableIds, setLoadingDisableIds] = useState<string[]>([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
  const [hasAnyLoops, setHasAnyLoops] = useState(false);
  const [viewDetailed, setViewDetailed] = useState(false);
  const [opensearchdashboardurl, setopensearchdashboardurl] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchVisible, setSearchVisible] = useState(false);
  const [loopNames, setLoopNames] = useState<string[]>([]);
  const [failedTeamspace, setFailedTeamspace] = useState<boolean>(false);
  const [incompleteLoopsCount, setIncompleteLoopsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isTeamspaceAdmin, setIsTeamspaceAdmin] = useState(false);
  const [isAnyAIModuleVisible, setIsAnyAIModuleVisible] = useState(false);
  const [dashboardLoaded, setDashboardLoaded] = useState(false);
  const [activeLoopName, setActiveLoopName] = useState('');

  // All useRef hooks
  const isInitialMount = useRef(true);
  const isMounted = useRef(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const fetchInProgressRef = useRef(false);
  const lastLoopsFetchAtRef = useRef(0);
  // Teamspace whose loops have already been rendered at least once. Used to tell
  // a first load (skeleton is correct) from a background refresh (skeleton would
  // destroy what is on screen) — see fetchLoops.
  const loadedTeamspaceRef = useRef<string | null>(null);
  // Late-bound reference to fetchLoops so earlier callbacks can trigger a
  // refresh without listing fetchLoops as a dep (fetchLoops is defined below).
  const fetchLoopsRef = useRef<() => Promise<void>>(() => Promise.resolve());

  // All useMemo hooks (IMPORTANT: services classes no longer call hooks internally; pass base URLs explicitly)
  const loopDataService = useMemo(
    () =>
      new LoopDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );
  const tenantDataService = useMemo(
    () =>
      new TenantDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );

  // All custom hooks that use other hooks internally
  useDashboardIframePolling(
    iframeRef,
    opensearchdashboardurl,
    setDashboardLoaded,
    dashboardLoaded,
    viewDetailed,
  );

  useDashboardRerenderInterval(setDashboardLoaded);

  // Derived values (not hooks)
  const showAI = services?.analyticsai?.show;
  const tValue = t;
  const showDisable = services?.disable?.show;
  const showSnackBarForToggleDisableLoop = useCallback(
    (message: string, type?: string) => {
      showSnackbar(message, type as 'success' | 'error' | 'info' | 'warning');
    },
    [showSnackbar],
  );

  // Poll a task href until it reports COMPLETED / FAILED or retries are exhausted.
  const pollTaskStatus = useCallback(
    async (link: string): Promise<string> => {
      let attempts = 0;
      while (attempts < (polling?.maxRetries ?? 200)) {
        attempts++;
        const resp = await tenantDataService.invokeHREF(link);
        if (resp.status === 'COMPLETED') return 'COMPLETED';
        if (resp.status === 'FAILED') return 'FAILED';
        await new Promise((res) => setTimeout(res, polling?.interval ?? 6000));
      }
      return 'FAILED';
    },
    [polling?.maxRetries, polling?.interval, tenantDataService],
  );

  // Removed client-side filtering logic; server does filtering now.

  const toggleDisableLoop = useCallback(
    async (loopId: string) => {
      if (!teamspaceId) return;

      const isCurrentlyDisabled = disableLoops.includes(loopId);
      const loop = allLoops.find((loop) => loop.id === loopId);
      try {
        setLoadingDisableIds((prev) => [...prev, loopId]);
        if (isCurrentlyDisabled) {
          const taskResp: LoopTaskResponse =
            await loopDataService.loopTaskUpdate(
              teamspaceId,
              loopId,
              'TOGGLE_LOOP',
              true,
            );

          const selfLink = taskResp?._links?.self?.href;
          const finalStatus = await pollTaskStatus(selfLink);
          if (finalStatus === 'COMPLETED') {
            await loopDataService.enableLoop(teamspaceId, loopId);
            setDisableLoops((prev) => {
              return isCurrentlyDisabled
                ? prev.filter((id) => id !== loopId) // Remove from disableLoops array if currently disabled
                : [...prev, loopId]; // Add to disableLoops array if not currently disabled
            });

            showSnackBarForToggleDisableLoop(
              `Loop ${loop?.displayName ?? ''} enabled successfully`,
              'success',
            );
          } else {
            showSnackBarForToggleDisableLoop(
              `Failed to enable Loop ${loop?.displayName ?? ''}`,
              'error',
            );
          }
        } else {
          const taskResp: LoopTaskResponse =
            await loopDataService.loopTaskUpdate(
              teamspaceId,
              loopId,
              'TOGGLE_LOOP',
              true,
            );

          const selfLink = taskResp?._links?.self?.href;
          const finalStatus = await pollTaskStatus(selfLink);
          if (finalStatus === 'COMPLETED') {
            await loopDataService.disableLoop(teamspaceId, loopId);
            showSnackBarForToggleDisableLoop(
              `Loop ${loop?.displayName || ''} disabled successfully`,
              'success',
            );
            setDisableLoops((prev) => {
              return isCurrentlyDisabled
                ? prev.filter((id) => id !== loopId) // Remove from disableLoops array if currently disabled
                : [...prev, loopId]; // Add to disableLoops array if not currently disabled
            });
          } else {
            showSnackBarForToggleDisableLoop(
              `Failed to disable Loop ${loop?.displayName ?? ''}`,
              'error',
            );
          }
        }
      } catch (error: any) {
        console.error('Error toggling Loop disable status:', error);
        setDisableLoops((prev) =>
          isCurrentlyDisabled
            ? [...prev, loopId]
            : prev.filter((id) => id !== loopId),
        );
        const errorType =
          error?.data?.type || error?.response?.data?.type || '';
        if (
          isCurrentlyDisabled &&
          errorType.includes('/automation/loop/limit-exceeded')
        ) {
          showSnackBarForToggleDisableLoop(
            t('learning-loop-enable-limit-exceeded'),
            'error',
          );
        } else {
          showSnackBarForToggleDisableLoop('Something went wrong', 'error');
        }
      } finally {
        setLoadingDisableIds((prev) => prev.filter((id) => id !== loopId));
        // Refresh loops after toggling disable status
        fetchLoopsRef.current();
      }
    },
    [
      disableLoops,
      loopDataService,
      teamspaceId,
      allLoops,
      pollTaskStatus,
      showSnackBarForToggleDisableLoop,
      t,
    ],
  );

  const toggleFavorite = useCallback(
    async (loopId: string) => {
      if (!teamspaceId) return;

      const isCurrentlyFavorite = favoriteLoops.includes(loopId);

      setFavoriteLoops((prev) => {
        return isCurrentlyFavorite
          ? prev.filter((id) => id !== loopId)
          : [...prev, loopId];
      });

      try {
        if (isCurrentlyFavorite) {
          await tenantDataService.removeTeamspaceFromFavorites(loopId);
        } else {
          await tenantDataService.addTeamspaceToFavorites(loopId);
        }
      } catch (error) {
        console.error('Error toggling loop favourite status:', error);
        setFavoriteLoops((prev) =>
          isCurrentlyFavorite
            ? [...prev, loopId]
            : prev.filter((id) => id !== loopId),
        );
      }
    },
    [favoriteLoops, tenantDataService, teamspaceId],
  );

  useEffect(() => {
    const checkIfTeamspaceCreationFailed = async () => {
      if (teamspaceId) {
        try {
          const teamspaceResp =
            await tenantDataService.getTeamspace(teamspaceId);
          if (teamspaceResp?.taskstatus === 'FAILED') {
            // If a task failed during teamspace creation then return user to creation step
            setFailedTeamspace(true);
          }
        } catch (error) {
          console.error('Error fetching teamspace:', error);
        }
      }
    };
    checkIfTeamspaceCreationFailed();
  }, [teamspaceId, tenantDataService]);

  useEffect(() => {
    setIsAdmin(roles.includes('ROLE_ADMIN'));
  }, [roles]);

  useEffect(() => {
    const fetchFavoritesOnce = async () => {
      if (!teamspaceId || isLoadingFavorites) return;

      try {
        setIsLoadingFavorites(true);
        const response =
          await tenantDataService.getFavoriteTeamspaces(teamspaceId);
        if (Array.isArray(response)) {
          setFavoriteLoops(response);
        } else {
          setFavoriteLoops([]);
        }
      } catch (error) {
        setFavoriteLoops([]);
      } finally {
        setIsLoadingFavorites(false);
      }
    };

    if (isInitialMount.current && teamspaceId) {
      fetchFavoritesOnce();
      isInitialMount.current = false;
    }
  }, [teamspaceId, tenantDataService, isLoadingFavorites]);

  useEffect(() => {}, [failedLoops]);

  // NEW EFFECT: Update isAnyAIModuleVisible when main AI module visibility changes
  useEffect(() => {
    setIsAnyAIModuleVisible(isAIModuleVisible);
  }, [isAIModuleVisible]);

  // Guard to prevent overlapping fetches which could create a loop if state changes quickly.

  const fetchLoops = useCallback(async () => {
    if (!teamspaceId) return;
    if (fetchInProgressRef.current) return;
    fetchInProgressRef.current = true;
    // `loading` swaps the whole content area for a skeleton (see the render
    // below), so setting it unmounts the loop cards, their embedded dashboard
    // iframes and any open AI panel — and remounts them fresh when the fetch
    // returns. That is correct for a first load, but this function also runs as
    // a background refresh (refreshIfStale, on window refocus once the data is
    // 60s old), where it reads to the user as the whole screen reloading and
    // silently discards component state. Only gate on the first load for a
    // given teamspace; a teamspace switch genuinely replaces the content, so it
    // still gets the skeleton.
    const isFirstLoadForTeamspace = loadedTeamspaceRef.current !== teamspaceId;
    if (isFirstLoadForTeamspace) setLoading(true);
    try {
      // Fetch ALL, DISABLED and INCOMPLETE loops, then merge.
      // Backend "all" endpoint may exclude incomplete loops; without merging we cannot render them (bug report).
      // Fetch the complete master set as a union of 'all' + 'disabled' +
      // 'incomplete'. We do NOT fetch 'recent' separately: it never contributed
      // loops to the master list (it was used only for a count badge), and the
      // Recent tab is derived client-side by sorting this master set by
      // createdTime. 'disabled' and 'incomplete' are kept because the backend
      // 'all' response can omit disabled/incomplete loops.
      //
      // Only ROLE_ADMIN / ROLE_SYSTEM callers may read disabled loops:
      // loop-server LoopService.searchLoops() rejects enabled=false for
      // non-elevated readers (hasElevatedLoopReadRole = admin || system) with a
      // 403 AccessDenied. A ROLE_USER therefore has no disabled loops it is
      // permitted to see, so we skip the request entirely rather than fire one
      // that is guaranteed to 403 and clutter dashboard init. Wrapping it in
      // .catch() alone was not enough — the failing request is still sent.
      const canReadDisabledLoops = roles.includes('ROLE_ADMIN');
      const [allResp, disabledResp, incompleteResp] = await Promise.all([
        fetchLoopsData(teamspaceId, loopDataService, 'all'),
        canReadDisabledLoops
          ? fetchLoopsData(teamspaceId, loopDataService, 'disabled').catch(
              () => [] as Loop[],
            )
          : Promise.resolve([] as Loop[]),
        fetchLoopsData(teamspaceId, loopDataService, 'incomplete').catch(
          () => [],
        ),
      ]);

      // Merge by id (prefer properties from 'all' when present). Include incomplete loops explicitly.
      const mergedById = new Map<string, Loop>();
      allResp.forEach((l) => mergedById.set(l.id, l));
      disabledResp.forEach((l) => {
        const existing = mergedById.get(l.id);
        mergedById.set(l.id, existing ? { ...l, ...existing } : l);
      });
      incompleteResp.forEach((l) => {
        const existing = mergedById.get(l.id);
        mergedById.set(l.id, existing ? { ...existing, ...l } : l);
      });
      const loopsData = Array.from(mergedById.values());

      // Derive incomplete/failed for context
      const newFailedLoops: failedLoop[] = [];
      const incompleteLoops: Loop[] = [];
      const allCompletedEnabledLoops: Loop[] = [];

      loopsData.forEach((loop) => {
        if (!loop.completed) {
          incompleteLoops.push(loop);
          if (isLoopFailed(loop)) {
            newFailedLoops.push({
              id: loop.id,
              name: loop.displayName,
              mode: 'loop',
              teamSpaceName: teamspaceName,
            });
          }
        } else if (loop.enabled && loop.completed) {
          allCompletedEnabledLoops.push(loop);
        }
      });

      setFailedLoops(newFailedLoops);
      setIncompleteLoops(incompleteLoops);
      setCompletedLoops(allCompletedEnabledLoops);
      // Cache first loop object for global access
      if (loopsData.length > 0) {
        if (!firstLoopData || firstLoopData.id !== loopsData[0].id) {
          const first = loopsData[0] as any; // loopsData already typed as Loop[]
          setFirstLoopData({
            createdTime: first.createdTime,
            id: first.id,
            displayName: first.displayName,
            tenantId: first.tenantId,
            teamSpaceId: first.teamSpaceId,
            workflowType: first.workflowType,
            enabled: first.enabled,
            associatedUserIds: first.associatedUserIds || [],
            description: first.description,
            failedTask: first.failedTask,
            _links: first._links || {
              self: { href: '' },
              loopEntity: { href: '' },
              modify: { href: '', type: '' },
              disable: { href: '', type: '' },
              users: { href: '' },
            },
          });
        }
      }

      // Favorites filtering (client-side) when favorites tab selected
      setAllLoops(allCompletedEnabledLoops);
      setLoops(loopsData); // keep for backward compatibility (now unfiltered master)
      setHasAnyLoops(loopsData.length > 0);

      const enabledCompleted = loopsData.filter(
        (l) => l.enabled && l.completed,
      );
      const disabled = loopsData.filter((l) => l.enabled === false);
      setDisableLoops(disabled.map((l) => l.id));
      setAllLoopsCount(enabledCompleted.length);
      setDisabledLoopsCount(disabled.length);
      setIncompleteLoopsCount(incompleteLoops.length);
      // Recent tab is derived client-side (enabled + completed loops sorted by
      // createdTime), so its count comes from the master list — no extra call.
      setRecentLoopsCount(allCompletedEnabledLoops.length);
    } catch (e) {
      setLoops([]);
      setAllLoops([]);
      setHasAnyLoops(false);
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
      lastLoopsFetchAtRef.current = Date.now();
      // Marked after any completed attempt, success or failure: either way the
      // content area now holds something (cards or the no-loops view), so a
      // later background refresh must not blank it out again.
      loadedTeamspaceRef.current = teamspaceId;
    }
  }, [
    teamspaceId,
    loopDataService,
    setFailedLoops,
    setIncompleteLoops,
    setCompletedLoops,
    teamspaceName,
    firstLoopData,
    setFirstLoopData,
  ]);

  // Keep the ref in sync so earlier-declared callbacks can call the latest
  // fetchLoops without listing it as a dep.
  useEffect(() => {
    fetchLoopsRef.current = fetchLoops;
  }, [fetchLoops]);

  // Tab counts (all/disabled/incomplete/recent) are derived directly from the
  // merged master list inside fetchLoops — see setAllLoopsCount /
  // setDisabledLoopsCount / setIncompleteLoopsCount / setRecentLoopsCount there.
  // A separate fetchLoopCounts() that re-hit all four filtered endpoints was
  // removed: with DEFAULT_PAGE_SIZE=999 the master list is complete, so counting
  // client-side yields the same totals without the extra round-trips.

  // Fetch loops and counts on initial mount
  useEffect(() => {
    fetchLoops();
    isMounted.current = true;
    // Run only once on mount; fetchLoops writes firstLoopData which is in its own
    // dep array, so including it here would risk a re-fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Re-fetch the master loop list only when the teamspace changes. The tab
  // filters (all/recent/disabled/incomplete/favorites) are applied client-side
  // to this master list, so switching tabs must NOT trigger a network refetch.
  // `isAdmin` and `selectedButton` were previously in the dependency list, which
  // caused (a) a duplicate initial fetch when isAdmin resolved after teamspaceId
  // and (b) a full 3-call refetch on every tab click — both pure waste.
  useEffect(() => {
    if (isMounted.current && teamspaceId) {
      fetchLoops();
    }
    // fetchLoops intentionally omitted: it depends on firstLoopData which it
    // updates, so including it can loop. Refetch is driven by teamspaceId only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamspaceId]);

  // Staleness middle ground: refresh the loop list when the user returns to the
  // tab, but only if the data is older than STALE_MS. Mutations (create / enable
  // / disable / upgrade) already refresh the list directly; this catches
  // external changes without the per-interaction refetch we removed.
  useEffect(() => {
    const STALE_MS = 60_000;
    const refreshIfStale = () => {
      if (
        document.visibilityState === 'visible' &&
        teamspaceId &&
        Date.now() - lastLoopsFetchAtRef.current > STALE_MS
      ) {
        fetchLoops();
      }
    };
    document.addEventListener('visibilitychange', refreshIfStale);
    window.addEventListener('focus', refreshIfStale);
    return () => {
      document.removeEventListener('visibilitychange', refreshIfStale);
      window.removeEventListener('focus', refreshIfStale);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamspaceId]);

  useEffect(() => {
    // When teamspaceId changes we need to fetch favorite loops again
    // so set isInitialMount.current back to true
    if (teamspaceId) {
      isInitialMount.current = true;
    }
  }, [teamspaceId]);

  useEffect(() => {
    if (allLoops.length > 0 && favoriteLoops.length > 0) {
      // Filter favorites to only include IDs that exist in current loops
      const existingLoopIds = allLoops.map((loop) => loop.id);
      const validFavorites = favoriteLoops.filter((favoriteId) =>
        existingLoopIds.includes(favoriteId),
      );

      // Update favorites if there's a difference
      if (validFavorites.length !== favoriteLoops.length) {
        setFavoriteLoops(validFavorites);
      }
    }
  }, [allLoops, favoriteLoops]);

  useEffect(() => {
    if (teamspaceId) {
      setViewDetailed(false);
    }
  }, [teamspaceId, teamspaceName]);

  useEffect(() => {
    const fetchTeamspaceUserRole = async () => {
      try {
        if (teamspaceId && user?.profile?.sub) {
          const roleResp = await tenantDataService.getUserTeamspaceRole(
            teamspaceId,
            user?.profile?.sub,
          );
          if (typeof roleResp === 'string') {
            setIsTeamspaceAdmin(roleResp === 'ROLE_ADMIN');
          } else if ('status' in roleResp && roleResp.status === 204) {
            // 204 status means the user could not be found
            setIsTeamspaceAdmin(false);
          }
        } else {
          setIsTeamspaceAdmin(false);
        }
      } catch (e) {
        console.error(
          "An error occurred retrieving the user's teamspace role: ",
          e,
        );
        setIsTeamspaceAdmin(false);
      }
    };
    fetchTeamspaceUserRole();
  }, [teamspaceId, user?.profile?.sub, tenantDataService]);

  useEffect(() => {
    setDashboardLoaded(false);
  }, [viewDetailed, opensearchdashboardurl]);

  // When no loops exist at all in the system (only after loading completes to prevent flash)
  // Also check that we've completed at least one fetch for this teamspace to prevent
  // showing "no loops" view during the brief moment between loading=false and data population
  const showNoLoopsView =
    !loading &&
    loadedTeamspaceRef.current === teamspaceId &&
    (isTeamspaceAdmin
      ? incompleteLoopsCount === 0 &&
        allLoops.length === 0 &&
        disabledLoopsCount === 0
      : allLoops.length === 0 && disabledLoopsCount === 0);

  const viewDetailedBoard = (
    data: boolean | ((prevState: boolean) => boolean),
    loopName?: string,
  ) => {
    setViewDetailed(data);
    if (loopName) setActiveLoopName(loopName);
  };

  const dashboardUrl = (data: React.SetStateAction<string>) => {
    setopensearchdashboardurl(data);
  };

  if (teamspaceName === INITIAL_TEAM_SPACE) {
    return (
      <div
        data-testid="loop-home-initial-teamspace"
        id="id-Loops"
        className="mx-auto w-[97.3%]"
      >
        <div
          id="id-loop-home-initial-header-wrapper"
          data-testid="loop-home-initial-header-wrapper"
        >
          <Header showFirstBar={false} />
        </div>
        <NoLoopsViewWrapper
          isAdmin={isAdmin}
          failedTeamspace={failedTeamspace}
          t={t}
          teamspaceName={teamspaceName}
          isTeamspaceAdmin={isTeamspaceAdmin}
        />
        {showAI && (
          <Tooltip title={t('create-loop-to-use-genie')} arrow>
            <img
              data-testid={'id-loop-genie-chat-icon-disabled'}
              src={AIChatDisabled}
              alt="AI Chat"
              className="fixed bottom-5 right-5 size-16 cursor-not-allowed opacity-50"
            />
          </Tooltip>
        )}
      </div>
    );
  }

  if (!viewDetailed) {
    return (
      <div
        data-testid="loop-home-page-container"
        id="id-Loops"
        className="flex size-full flex-col"
      >
        <div
          id="id-loop-home-header-wrapper"
          data-testid="loop-home-header-wrapper"
        >
          <Header showFirstBar={false} />
        </div>
        {loading ? (
          <div
            id="id-loop-home-loading-skeleton"
            data-testid="loop-home-loading-skeleton"
            className="flex-1 overflow-auto p-4"
          >
            <div className="animate-pulse space-y-4">
              <div className="h-8 w-1/3 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-48 rounded-lg bg-gray-200 dark:bg-gray-700" />
            </div>
          </div>
        ) : showNoLoopsView ? (
          <div
            id="id-loop-home-no-loops-content"
            data-testid="loop-home-no-loops-content"
            className="flex-1 overflow-auto"
          >
            <NoLoopsViewWrapper
              isAdmin={isAdmin}
              failedTeamspace={failedTeamspace}
              t={t}
              teamspaceName={teamspaceName}
              isTeamspaceAdmin={isTeamspaceAdmin}
            />
          </div>
        ) : loading ? (
          <div
            id="id-loop-home-loading"
            data-testid="loop-home-loading"
            className="flex-1 overflow-auto bg-[#f4f4f4] p-4 dark:bg-[#161616]"
          >
            <div className="mx-auto space-y-4 rounded-lg bg-white p-6 pt-4 dark:bg-[#262626]">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        ) : (
          <div
            id="id-loop-home-content-area"
            data-testid="loop-home-content-area"
            className="flex-1 overflow-auto bg-[#f4f4f4] p-4 dark:bg-[#161616]"
          >
            <div
              id="id-loop-home-loops-panel"
              data-testid="loop-home-loops-panel"
              className="mx-auto rounded-lg bg-white p-6 pt-4 dark:bg-[#262626]"
            >
              <LoopsList
                loops={loops}
                isAdmin={isAdmin}
                selectedButton={selectedButton}
                setSelectedButton={setSelectedButton}
                allLoopsCount={allLoopsCount}
                disabledLoopsCount={disabledLoopsCount}
                favoriteLoops={favoriteLoops}
                t={t}
                disableLoops={disableLoops}
                toggleDisableLoop={toggleDisableLoop}
                loadingDisableIds={loadingDisableIds}
                toggleFavorite={toggleFavorite}
                fetchLoops={fetchLoops}
                chatHistories={chatHistories}
                setChatHistories={setChatHistories}
                teamspaceId={teamspaceId}
                hasAnyLoops={hasAnyLoops}
                dashboardUrl={dashboardUrl}
                viewDetailedBoard={viewDetailedBoard}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                showDisable={showDisable ?? false}
                incompleteLoopsCount={incompleteLoopsCount}
                isTeamspaceAdmin={isTeamspaceAdmin}
                searchVisible={searchVisible}
                setSearchVisible={setSearchVisible}
                onFilterChange={() => {
                  setSearchQuery('');
                  setSearchVisible(false);
                }}
                isAnyAIModuleVisible={isAnyAIModuleVisible}
                onAIModuleVisibilityChange={setIsAnyAIModuleVisible}
                allLoops={allLoops}
              />
            </div>
          </div>
        )}
        <CustomSnackbar
          open={snackbarState.open}
          message={snackbarState.message}
          severity={snackbarState.severity}
          onClose={closeSnackbar}
        />
        {showAI && (
          <>
            <AIModule
              isVisible={isAIModuleVisible}
              onClose={() => setIsAIModuleVisible(false)}
              availableLoops={loops.map((loop) => ({
                id: loop.id,
                name: loop.displayName,
              }))}
              initialChatHistories={chatHistories}
              setChatHistories={setChatHistories}
            />
          </>
        )}
      </div>
    );
  } else {
    return (
      <div
        data-testid="loop-home-detailed-view"
        id=""
        className="flex w-full flex-col p-2.5"
      >
        <div
          id="id-loop-home-breadcrumb"
          data-testid="loop-home-breadcrumb"
          className="w-full"
        >
          <span
            id="id-loop-home-breadcrumb-home"
            data-testid="loop-home-breadcrumb-home"
            role="button"
            tabIndex={0}
            onClick={() => {
              setViewDetailed(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setViewDetailed(false);
              }
            }}
            className={`text-sm font-medium text-[#0066B0] ${
              isHovered ? 'cursor-pointer' : 'cursor-auto'
            }`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            Home
          </span>
          <p className="inline px-2.5">|</p>
          <span
            id="id-loop-home-breadcrumb-dashboards"
            data-testid="loop-home-breadcrumb-dashboards"
            role="button"
            tabIndex={0}
            onClick={() => {
              setViewDetailed(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setViewDetailed(false);
              }
            }}
            className={`text-sm font-medium text-[#0066B0] ${
              isHovered ? 'cursor-pointer' : 'cursor-auto'
            }`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {t('dashboards')}
          </span>
          <p className="inline px-2.5">|</p>
          <span
            id="id-loop-home-breadcrumb-active"
            data-testid="loop-home-breadcrumb-active"
            className="text-sm font-medium text-[#0066B0]"
          >
            {activeLoopName
              ? `${activeLoopName}~${t('detail-dashboard')}`
              : t('view-dashboard')}
          </span>
        </div>

        <div
          id="id-loop-home-details-title-wrapper"
          data-testid="loop-home-details-title-wrapper"
          className="mt-2.5 w-full"
        >
          <span
            id="id-loop-home-details-title"
            data-testid="loop-home-details-title"
            className="text-lg font-semibold text-gray-900 dark:text-white"
          >
            {t('details')}
          </span>
        </div>

        <div
          id="id-loop-home-dashboard-wrapper"
          data-testid="loop-home-dashboard-wrapper"
          className="relative w-full"
        >
          <iframe
            src={sanitizeIframeSrc(opensearchdashboardurl)}
            ref={iframeRef}
            title="Detailed Dashboard"
            className={`h-screen w-full border-none transition-opacity duration-200 ${
              dashboardLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          ></iframe>
          {!dashboardLoaded && (
            <div
              id="id-skeleton-overlay"
              data-testid="skeleton-overlay"
              className="absolute inset-0 flex flex-col gap-4 bg-white p-6 dark:bg-carbon-darkPanel"
            >
              {/* Title skeleton */}
              <Skeleton className="h-6 w-1/4" />
              {/* Chart area skeleton */}
              <Skeleton className="h-64 w-full" />
              {/* Bottom row skeletons */}
              <div
                id="id-loop-home-skeleton-bottom-row"
                data-testid="loop-home-skeleton-bottom-row"
                className="flex gap-4"
              >
                <Skeleton className="h-24 w-1/3" />
                <Skeleton className="h-24 w-1/3" />
                <Skeleton className="h-24 w-1/3" />
              </div>
              <div
                id="id-loop-home-skeleton-text-row"
                data-testid="loop-home-skeleton-text-row"
                className="flex gap-4"
              >
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/5" />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default withZoom(LoopHome);
